import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Dumbbell, Apple, Heart, ShoppingCart, MoreVertical } from 'lucide-react';
import { Program } from '../../types/program';
import { programService } from '../../services/programService';
import { showToast } from '../../components/Toast';
import { showConfirm } from '../../components/ConfirmDialog';
import { getFullMediaUrl } from '../../utils/urlHelper';

const ProgramOverview: React.FC = () => {
  const navigate = useNavigate();
  const { id: programId } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'workout' | 'nutrition' | 'recovery' | 'shopping'>('workout');
  const [program, setProgram] = useState<Program | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    const loadProgramData = async () => {
      // Double-check before making API call
      if (!programId || programId === 'create' || programId === 'undefined') {
        console.log('loadProgramData called with invalid ID, aborting');
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        console.log('Fetching program with ID:', programId);
        const data = await programService.getProgramById(programId);
        console.log('Program data received:', data);
        console.log('Nutrition Plan:', data.nutritionPlan);
        console.log('Recovery Protocol:', data.recoveryProtocol);
        console.log('Shopping List:', data.shoppingList);
        setProgram(data);
      } catch (error: any) {
        console.error('Error loading program:', error);
        const errorMessage = error?.response?.status === 404 
          ? 'Program not found' 
          : 'Failed to load program data';
        showToast(errorMessage, 'error');
        setProgram(null);
      } finally {
        setIsLoading(false);
      }
    };

    console.log('ProgramOverview - programId:', programId);
    
    // Check for invalid IDs including the string "undefined"
    if (!programId || programId === 'create' || programId === 'undefined') {
      console.log('Invalid programId, redirecting to /programs');
      navigate('/programs', { replace: true });
      return;
    }
    
    console.log('Loading program data for ID:', programId);
    loadProgramData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, navigate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleDeleteExercise = async (dayIndex: number, exerciseIndex: number) => {
    if (!program || !programId) return;

    try {
      // Create updated program data
      const updatedDays = [...program.versions[0].days];
      updatedDays[dayIndex].exercises.splice(exerciseIndex, 1);

      const updatedProgram = {
        ...program,
        versions: [{
          ...program.versions[0],
          days: updatedDays
        }]
      };

      // Update program via API
      await programService.updateProgram(programId, updatedProgram);
      
      // Update local state
      setProgram(updatedProgram);
      setOpenDropdown(null);
      showToast('Exercise deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting exercise:', error);
      showToast('Failed to delete exercise', 'error');
    }
  };

  const tabs = [
    { id: 'workout' as const, label: 'Workout Plan', icon: Dumbbell },
    { id: 'nutrition' as const, label: 'Nutrition', icon: Apple },
    { id: 'recovery' as const, label: 'Recovery', icon: Heart },
    { id: 'shopping' as const, label: 'Shopping List', icon: ShoppingCart },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1F1F1F] border-t-[#23F47C] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#8F8F8F]">Loading program...</p>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-[#8F8F8F] mb-4">Program not found</p>
          <button
            onClick={() => navigate('/programs')}
            className="px-6 py-2 bg-[#23F47C] text-[#0A0A0A] rounded-lg hover:bg-[#1FD66D] transition-all"
          >
            Back to Programs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1920px] mx-auto min-h-screen bg-[#0A0A0A]">
      {/* Header with background image */}
      <div 
        className="relative h-[280px] bg-cover bg-center"
        style={{
          backgroundImage: program.imageUrl 
            ? `linear-gradient(to bottom, rgba(10,10,10,0.3), rgba(10,10,10,0.9)), url(${getFullMediaUrl(program.imageUrl)})`
            : 'linear-gradient(135deg, #1F1F1F 0%, #141414 100%)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A0A0A]"></div>
        
        {/* Back button */}
        <button
          onClick={() => navigate('/programs')}
          className="absolute top-6 left-6 w-9 h-9 bg-[rgba(31,31,31,0.8)] backdrop-blur-sm border border-[#1F1F1F] rounded-lg flex items-center justify-center text-white hover:bg-[#1F1F1F] transition-all"
        >
          <ArrowLeft size={18} />
        </button>

        {/* Edit button */}
        <button
          onClick={() => navigate(`/programs/${programId}/edit`)}
          className="absolute top-6 right-6 inline-flex items-center gap-2 h-11 px-6 bg-[#23F47C] text-[#0A0A0A] text-base font-semibold rounded-lg hover:bg-[#1FD66D] transition-all"
        >
          <Edit size={18} />
          <span>Edit Program</span>
        </button>

        {/* Program title and info */}
        <div className="absolute bottom-6 left-6 right-6">
          <h1 className="text-[40px] font-bold text-white leading-[48px] tracking-[-0.5px] mb-2">
            {program.title}
          </h1>
          <p className="text-[16px] text-[#8F8F8F] mb-3">
            {program.durationWeeks}-Week {program.difficulty.charAt(0).toUpperCase() + program.difficulty.slice(1)} Transformation Program • Level: {program.difficulty.charAt(0).toUpperCase() + program.difficulty.slice(1)}
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 px-6 border-b-2 border-[#1F1F1F] bg-[#0A0A0A]">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 h-14 px-5 text-[15px] font-semibold border-b-2 transition-all duration-200 relative -mb-0.5 ${
                activeTab === tab.id
                  ? 'text-[#23F47C] border-[#23F47C]'
                  : 'text-[#8F8F8F] border-transparent hover:text-white'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {activeTab === 'workout' && (
              <>
                {program.versions[0]?.days.map((day, dayIndex) => (
                  <div key={dayIndex} className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-white">
                        Day {day.dayNumber}: {day.name}
                      </h3>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-[#23F47C] font-semibold">Strength Focus</span>
                        <span className="text-sm text-[#8F8F8F]">
                          {day.exercises.reduce((total, ex) => total + (ex.sets * parseInt(ex.reps) || 0), 0)} Minutes
                        </span>
                      </div>
                    </div>

                    {/* Exercises */}
                    <div className="space-y-4">
                      {day.exercises.map((exercise, exIndex) => (
                        <div key={exIndex} className="flex gap-4 p-4 bg-[#0A0A0A] rounded-lg border border-[#1F1F1F]">
                          {/* Exercise Image/Video Thumbnail */}
                          <div className="w-20 h-20 bg-[#1F1F1F] rounded-lg flex-shrink-0 overflow-hidden">
                            {exercise.videoUrl ? (
                              <img 
                                src={getFullMediaUrl(exercise.videoUrl) || ''} 
                                alt={exercise.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Dumbbell size={32} className="text-[#5F5F5F]" />
                              </div>
                            )}
                          </div>

                          {/* Exercise Details */}
                          <div className="flex-1">
                            <h4 className="text-white font-semibold mb-1">{exercise.name}</h4>
                            <p className="text-sm text-[#8F8F8F] mb-3">{exercise.description}</p>
                            
                            {/* Exercise Stats */}
                            <div className="flex items-center gap-6 text-sm">
                              <div>
                                <span className="text-[#5F5F5F] uppercase text-xs">SETS</span>
                                <p className="text-[#23F47C] font-semibold">{exercise.sets} Sets</p>
                              </div>
                              <div>
                                <span className="text-[#5F5F5F] uppercase text-xs">REPS</span>
                                <p className="text-white font-semibold">{exercise.reps} Reps</p>
                              </div>
                              <div>
                                <span className="text-[#5F5F5F] uppercase text-xs">REST</span>
                                <p className="text-white font-semibold">{exercise.restTimeSec} Sec</p>
                              </div>
                        
                            </div>
                          </div>

                          {/* More Options */}
                          <div className="relative">
                            <button 
                              className="text-[#8F8F8F] hover:text-white p-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                const dropdownId = `${dayIndex}-${exIndex}`;
                                setOpenDropdown(openDropdown === dropdownId ? null : dropdownId);
                              }}
                            >
                              <MoreVertical size={20} />
                            </button>
                            
                            {/* Dropdown Menu */}
                            {openDropdown === `${dayIndex}-${exIndex}` && (
                              <div className="absolute right-0 top-full mt-1 w-40 bg-[#1F1F1F] border border-[#2F2F2F] rounded-lg shadow-lg z-10">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    showConfirm({
                                      title: 'Delete Exercise',
                                      message: `Are you sure you want to delete "${exercise.name}"? This action cannot be undone.`,
                                      confirmText: 'Delete',
                                      cancelText: 'Cancel',
                                      type: 'danger',
                                      onConfirm: () => handleDeleteExercise(dayIndex, exIndex),
                                    });
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-red-500 hover:bg-[#2F2F2F] rounded-lg transition-colors"
                                >
                                  Delete Exercise
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {(!program.versions[0]?.days || program.versions[0].days.length === 0) && (
                  <div className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-12 text-center">
                    <Dumbbell size={48} className="text-[#5F5F5F] mx-auto mb-4" />
                    <p className="text-[#8F8F8F]">No workout days configured yet</p>
                  </div>
                )}
              </>
            )}

            {activeTab === 'nutrition' && (
              <div className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
                <h3 className="text-xl font-bold text-white mb-6">Nutrition Plan</h3>
                {program.nutritionPlan ? (
                  <div className="space-y-6">
                    {/* Macro Targets */}
                    <div>
                      <h4 className="text-sm font-semibold text-[#8F8F8F] mb-3">Daily Targets</h4>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="bg-[#0A0A0A] p-4 rounded-lg">
                          <p className="text-xs text-[#5F5F5F] mb-1">Calories</p>
                          <p className="text-2xl font-bold text-white">{program.nutritionPlan.macroTargets.calories}</p>
                        </div>
                        <div className="bg-[#0A0A0A] p-4 rounded-lg">
                          <p className="text-xs text-[#5F5F5F] mb-1">Protein</p>
                          <p className="text-2xl font-bold text-[#23F47C]">{program.nutritionPlan.macroTargets.protein}g</p>
                        </div>
                        <div className="bg-[#0A0A0A] p-4 rounded-lg">
                          <p className="text-xs text-[#5F5F5F] mb-1">Carbs</p>
                          <p className="text-2xl font-bold text-white">{program.nutritionPlan.macroTargets.carbs}g</p>
                        </div>
                        <div className="bg-[#0A0A0A] p-4 rounded-lg">
                          <p className="text-xs text-[#5F5F5F] mb-1">Fats</p>
                          <p className="text-2xl font-bold text-white">{program.nutritionPlan.macroTargets.fats}g</p>
                        </div>
                      </div>
                    </div>

                    {/* Meals */}
                    {program.nutritionPlan.meals && program.nutritionPlan.meals.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-[#8F8F8F] mb-3">Meal Plan</h4>
                        <div className="space-y-3">
                          {program.nutritionPlan.meals.map((meal, index) => (
                            <div key={index} className="bg-[#0A0A0A] p-4 rounded-lg">
                              <h5 className="text-white font-semibold mb-1">{meal.title}</h5>
                              {meal.description && (
                                <p className="text-sm text-[#8F8F8F] mb-2">{meal.description}</p>
                              )}
                              <div className="flex gap-4 text-xs text-[#8F8F8F]">
                                <span>{meal.nutrition.calories} cal</span>
                                <span>P: {meal.nutrition.protein}g</span>
                                <span>C: {meal.nutrition.carbs}g</span>
                                <span>F: {meal.nutrition.fats}g</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Apple size={48} className="text-[#5F5F5F] mx-auto mb-4" />
                    <p className="text-[#8F8F8F]">No nutrition plan configured</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'recovery' && (
              <div className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
                <h3 className="text-xl font-bold text-white mb-6">Recovery Protocol</h3>
                {program.recoveryProtocol && program.recoveryProtocol.items.length > 0 ? (
                  <div className="space-y-4">
                    {program.recoveryProtocol.items.map((item, index) => (
                      <div key={index} className="bg-[#0A0A0A] p-4 rounded-lg flex items-start gap-4">
                        <div className="w-10 h-10 bg-[rgba(35,244,124,0.1)] rounded-lg flex items-center justify-center flex-shrink-0">
                          <Heart size={20} className="text-[#23F47C]" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                          {item.description && (
                            <p className="text-sm text-[#8F8F8F] mb-2">{item.description}</p>
                          )}
                          <p className="text-xs text-[#23F47C]">{item.duration} minutes</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Heart size={48} className="text-[#5F5F5F] mx-auto mb-4" />
                    <p className="text-[#8F8F8F]">No recovery protocol configured</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'shopping' && (
              <div className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
                <h3 className="text-xl font-bold text-white mb-6">Shopping List</h3>
                {program.shoppingList && program.shoppingList.items.length > 0 ? (
                  <div className="space-y-4">
                    {program.shoppingList.items.map((item, index) => (
                      <div key={index} className="bg-[#0A0A0A] p-4 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#1F1F1F] rounded flex items-center justify-center">
                            <ShoppingCart size={16} className="text-[#8F8F8F]" />
                          </div>
                          <div>
                            <h4 className="text-white font-medium">{item.name}</h4>
                            {item.notes && (
                              <p className="text-xs text-[#8F8F8F]">{item.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-semibold">{item.quantity} {item.unit}</p>
                          {item.category && (
                            <p className="text-xs text-[#8F8F8F] capitalize">{item.category}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ShoppingCart size={48} className="text-[#5F5F5F] mx-auto mb-4" />
                    <p className="text-[#8F8F8F]">No shopping list configured</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Nutrition Target Card */}
            {program.nutritionPlan && (
              <div className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-[rgba(35,244,124,0.1)] rounded flex items-center justify-center">
                    <Apple size={16} className="text-[#23F47C]" />
                  </div>
                  <h3 className="text-white font-semibold">Nutrition Target</h3>
                </div>
                
                <div className="mb-4">
                  <p className="text-xs text-[#8F8F8F] mb-1">Daily Calories</p>
                  <p className="text-3xl font-bold text-[#23F47C]">
                    {program.nutritionPlan.macroTargets.calories.toLocaleString()} 
                    <span className="text-sm text-[#8F8F8F] ml-1">KCAL</span>
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-[#8F8F8F] uppercase">Protein</span>
                    <span className="text-white font-semibold">{program.nutritionPlan.macroTargets.protein}g</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-[#8F8F8F] uppercase">Carbs</span>
                    <span className="text-white font-semibold">{program.nutritionPlan.macroTargets.carbs}g</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-[#8F8F8F] uppercase">Fats</span>
                    <span className="text-white font-semibold">{program.nutritionPlan.macroTargets.fats}g</span>
                  </div>
                </div>
              </div>
            )}

            {/* Recovery Protocol Card */}
            {program.recoveryProtocol && program.recoveryProtocol.items.length > 0 && (
              <div className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-[rgba(35,244,124,0.1)] rounded flex items-center justify-center">
                    <Heart size={16} className="text-[#23F47C]" />
                  </div>
                  <h3 className="text-white font-semibold">Recovery Protocol</h3>
                </div>
                
                <div className="space-y-3">
                  {program.recoveryProtocol.items.slice(0, 3).map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#0A0A0A] rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Heart size={12} className="text-[#23F47C]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">{item.title}</p>
                        <p className="text-xs text-[#8F8F8F]">Target {item.duration} Min</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Weekly Schedule Card */}
            <div className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
              <h3 className="text-white font-semibold mb-4">Weekly Schedule</h3>
              
              <div className="grid grid-cols-7 gap-2">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => {
                  const hasWorkout = program.versions[0]?.days.some(d => d.dayNumber === index + 1);
                  return (
                    <div key={index} className="text-center">
                      <p className="text-xs text-[#8F8F8F] mb-2">{day}</p>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto ${
                        hasWorkout ? 'bg-[#23F47C]' : 'bg-[#1F1F1F]'
                      }`}>
                        {hasWorkout && (
                          <div className="w-2 h-2 bg-[#0A0A0A] rounded-full"></div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramOverview;
